import { useTranslation } from 'next-i18next';

import {
    Box,
    Button,
    Flex,
    Heading,
    Image,
    Link,
    Text,
    useSlotRecipe,
    VStack,
} from '@chakra-ui/react';

import { urlMap } from '@nocowanie/core/consts';
import { urlHelpers } from '@nocowanie/core/helpers';
import { IconsLinear } from '@nocowanie/icons/linear';

import { getAssetUrl } from '@app/helpers/ssr.helpers';

import { SupportProps } from './support.props';

export const Support = ({ ...props }: SupportProps) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'support',
    });

    const touristInfoItems = {
        userType: t('iAmTourist'),
        email: 'info@nocowanie.pl',
        phoneNumber: '22 116 82 96',
        openingDays: t('openingDays'),
        openingHours: '8:00 - 20:00',
        openingDaysWeekend: t('openingDaysWeekend'),
        openingHoursWeekend: '9:00-17:00',
    };

    const recipe = useSlotRecipe({ key: 'support' });
    const styles = recipe();

    const bgSrc = getAssetUrl('support/bg-support.webp');

    return (
        <Box css={styles.bgWrapper} {...props}>
            <Flex css={styles.container}>
                <Flex css={styles.contactInfoBox}>
                    <Heading size={'md'} mb={6}>
                        {t('supportHeader')}
                    </Heading>
                    <Text
                        hideBelow={'md'}
                        dangerouslySetInnerHTML={{ __html: `${t('supportSubHeader')}` }}
                        css={styles.subHeader}
                    />
                    <Flex css={styles.userTypesWrapper}>
                        <VStack css={styles.userTypeBox} alignItems={'start'}>
                            <Text
                                mb={2}
                                fontSize={'lg'}
                                dangerouslySetInnerHTML={{ __html: t('iAmHost') }}
                            />
                            <Button asChild colorPalette={'secondary'}>
                                <a target="_blank" href={urlMap.partnerContactForm}>
                                    {t('fillInForm')}
                                </a>
                            </Button>
                        </VStack>
                        <VStack
                            css={styles.userTypeBox}
                            alignItems={'start'}
                            justifyContent={'space-between'}
                        >
                            <Text
                                mb={2}
                                fontSize={'lg'}
                                dangerouslySetInnerHTML={{ __html: touristInfoItems.userType }}
                            />
                            <Flex alignItems={'center'} gap={2} mb={2}>
                                <IconsLinear.SmsTracking fontSize={'xl'} />
                                <Link
                                    href={`mailto:${touristInfoItems.email}`}
                                    css={styles.emailLink}
                                    variant={'plain'}
                                >
                                    {touristInfoItems.email}
                                </Link>
                            </Flex>
                            <Flex direction={'column'}>
                                <Flex gap={2} alignItems={'center'}>
                                    <IconsLinear.Call2 fontSize={'xl'} />
                                    <Link
                                        href={urlHelpers.getPhoneNoHref(
                                            touristInfoItems.phoneNumber,
                                        )}
                                        variant={'plain'}
                                        colorPalette={'gray'}
                                        fontSize={'md'}
                                    >
                                        {touristInfoItems.phoneNumber}
                                    </Link>
                                </Flex>
                                <Text css={styles.hotlineAvailability}>
                                    {touristInfoItems.openingDays} {touristInfoItems.openingHours}
                                    <br />
                                    {touristInfoItems.openingDaysWeekend}{' '}
                                    {touristInfoItems.openingHoursWeekend}
                                </Text>
                            </Flex>
                        </VStack>
                    </Flex>
                </Flex>
                <Flex css={styles.checkOurOfferBox}>
                    <Box css={styles.image}>
                        <Image
                            fit={'contain'}
                            alt=""
                            src={bgSrc}
                            w={'full'}
                            h={'full'}
                            htmlWidth={280}
                            htmlHeight={156}
                            loading="lazy"
                        />
                    </Box>
                    <Text
                        dangerouslySetInnerHTML={{ __html: `${t('addAccommodation')}` }}
                        fontWeight={'600'}
                        fontSize={'xl'}
                    />
                    <Button asChild css={styles.button} variant={'solid'}>
                        <a target="_blank" href={`/${urlMap.join}`}>
                            {t('checkOurOffer')}
                        </a>
                    </Button>
                </Flex>
            </Flex>
        </Box>
    );
};
